import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type CreditTier = "free" | "lite" | "pro";

export interface CreditStatus {
  on_new_billing: boolean;
  tier: CreditTier;
  credits_granted: number | null;
  credits_used: number;
  credits_remaining: number | null;
  period_start: string;
  period_end: string;
  hired_contacts_used: number;
  hired_contacts_cap: number;
  subscription: {
    status: string | null;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
  } | null;
  /** A live Search Sprint (one payment, nothing renews). Absent on older deploys. */
  sprint?: { starts_at: string | null; ends_at: string } | null;
}

export const CREDIT_STATUS_KEY = ["credit-status"] as const;

function useUserId() {
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setUserId(data.session?.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  return userId;
}

export function useCreditStatus() {
  const userId = useUserId();

  return useQuery({
    queryKey: [...CREDIT_STATUS_KEY, userId ?? "anon"],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<CreditStatus | null> => {
      const { data, error } = await supabase.functions.invoke("get-credit-status");
      if (error) throw error;
      return (data as CreditStatus) ?? null;
    },
  });
}

export function useInvalidateCreditStatus() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: CREDIT_STATUS_KEY });
}
